import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs, { type TabsProps } from "@mui/material/Tabs";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { ArchiveFieldModal } from "@/pages/applications/pages/customFields/components/ArchiveFieldModal";
import { CreateFieldModal } from "@/pages/applications/pages/customFields/components/CreateFieldModal";
import { CustomFieldTable } from "@/pages/applications/pages/customFields/components/CustomFieldTable";
import { EditFieldModal } from "@/pages/applications/pages/customFields/components/EditFieldModal";
import { RestoreFieldModal } from "@/pages/applications/pages/customFields/components/RestoreFieldModal";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

const TabsSchema = z.enum(["active", "archived"]);
type TabsSchemaType = z.infer<typeof TabsSchema>;

interface TabPanelProps extends React.ComponentProps<"div"> {
  index: TabsSchemaType;
  value: TabsSchemaType;
}

const TabPanel: React.FC<TabPanelProps> = function TabPanel({
  children,
  value,
  index,
  ...other
}) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`custom-fields-tabpanel-${index}`}
      aria-labelledby={`custom-fields-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const styles = {
  link: {
    textDecoration: "none",
  },
  pageTitle: {
    marginBottom: 0,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    width: "100%",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    width: "100%",
  },
  description: {
    flex: 1,
    fontSize: "14px",
    color: theme.colors.staticFgNote,
    lineHeight: 1.43,
  },
  headerActions: {
    display: "flex",
    gap: "8px",
    flexShrink: 0,
    alignItems: "center",
  },
  limitText: {
    whiteSpace: "nowrap",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const CustomFieldsPageInternal: React.FC = function CustomFieldsPageInternal() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState<TabsSchemaType>(
    TabsSchema.Enum.active,
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  /**
   * The field being edited is held by id, not by value. A snapshot taken when
   * the row was clicked can never see a refetch, and the option editor depends
   * on seeing one: losing a save to a concurrent edit reloads the list, and
   * the modal has to reseed from what came back.
   */
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);
  const [archivingField, setArchivingField] = useState<
    InterludeTypes["CustomField"] | null
  >(null);
  const [restoringField, setRestoringField] = useState<
    InterludeTypes["CustomField"] | null
  >(null);

  const customFieldsQuery = interlude.customField.useListCustomFields({
    // Ask for single select fields and their options. Interlude leaves
    // them out unless a caller opts in.
    queries: { enable_single_select: true },
  });
  const organizationSettingQuery =
    interlude.organization.useOrganizationSetting();

  const customFieldLimit = organizationSettingQuery.data?.customFieldLimit ?? 0;

  const editingField = useMemo(
    () =>
      editingFieldId === null
        ? null
        : (customFieldsQuery.data?.find(
            (field) => field.id === editingFieldId,
          ) ?? null),
    [customFieldsQuery.data, editingFieldId],
  );

  const activeFields = useMemo(() => {
    return customFieldsQuery.data?.filter((field) => field.active) ?? [];
  }, [customFieldsQuery.data]);

  const archivedFields = useMemo(() => {
    return customFieldsQuery.data?.filter((field) => !field.active) ?? [];
  }, [customFieldsQuery.data]);

  const activeCount = activeFields.length;
  const archivedCount = archivedFields.length;
  const isLimitReached = activeCount >= customFieldLimit;

  const handleTabChange = useHandler<TabsProps["onChange"]>((_, newValue) => {
    const result = TabsSchema.safeParse(newValue);
    if (!result.success) return;
    setTabValue(result.data);
  });

  const handleCreateClick = useHandler(() => {
    setIsCreateModalOpen(true);
  });

  const handleCloseCreateModal = useHandler(() => {
    setIsCreateModalOpen(false);
  });

  const handleCreateSuccess = useHandler(() => {
    enqueueSnackbar(t("customField.toast.createSuccess"), {
      variant: "success",
    });
  });

  const handleEdit = useHandler((field: InterludeTypes["CustomField"]) => {
    setEditingFieldId(field.id);
  });

  const handleCloseEditModal = useHandler(() => {
    setEditingFieldId(null);
  });

  const handleEditSuccess = useHandler(() => {
    enqueueSnackbar(t("customField.toast.updateSuccess"), {
      variant: "success",
    });
  });

  const handleArchive = useHandler((field: InterludeTypes["CustomField"]) => {
    setArchivingField(field);
  });

  const handleCloseArchiveModal = useHandler(() => {
    setArchivingField(null);
  });

  const handleArchiveSuccess = useHandler(() => {
    enqueueSnackbar(t("customField.toast.archiveSuccess"), {
      variant: "success",
    });
  });

  const handleRestore = useHandler((field: InterludeTypes["CustomField"]) => {
    setRestoringField(field);
  });

  const handleCloseRestoreModal = useHandler(() => {
    setRestoringField(null);
  });

  const handleRestoreSuccess = useHandler(() => {
    enqueueSnackbar(t("customField.toast.restoreSuccess"), {
      variant: "success",
    });
  });

  const createButton: React.ReactNode = useMemo(() => {
    const button = (
      <Button
        variant="primary"
        onClick={handleCreateClick}
        disabled={isLimitReached}
      >
        {t("customField.page.createButton")}
      </Button>
    );

    if (isLimitReached) {
      return (
        <Tooltip title={t("customField.page.limitReached")} arrow>
          <span>{button}</span>
        </Tooltip>
      );
    }

    return button;
  }, [handleCreateClick, isLimitReached, t]);

  return (
    <Stack gap="8px">
      <Breadcrumbs aria-label="breadcrumb" separator=">">
        <Box component={Link} to="/applications" sx={styles.link}>
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t("applications.title")}
          </Typography>
        </Box>
        <Typography variant="body" color={theme.colors.staticFgNote}>
          {t("customField.page.title")}
        </Typography>
      </Breadcrumbs>
      <Box sx={styles.pageTitle}>
        <PageTitle>{t("customField.page.title")}</PageTitle>
      </Box>

      <Box sx={styles.section}>
        {/* Header: description + limit + add button */}
        <Box sx={styles.headerRow}>
          <Box sx={styles.description}>
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {t("customField.page.description")}
            </Typography>
          </Box>
          <Box sx={styles.headerActions}>
            <Typography variant="body" color={theme.colors.staticFgNote}>
              <Box component="span" sx={styles.limitText}>
                {t("customField.page.totalActiveFields")}
                {activeCount}/{customFieldLimit}
              </Box>
            </Typography>
            {createButton}
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            label={
              <Stack direction="row" alignItems="center" gap={1}>
                {t("customField.page.tabs.active")}
                <Badge variant="superior">{activeCount}</Badge>
              </Stack>
            }
            value={TabsSchema.Values.active}
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" gap={1}>
                {t("customField.page.tabs.archived")}
                <Badge variant="plain">{archivedCount}</Badge>
              </Stack>
            }
            value={TabsSchema.Values.archived}
          />
        </Tabs>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={TabsSchema.Enum.active}>
          <CustomFieldTable
            variant="active"
            fields={activeFields}
            isLoading={customFieldsQuery.isPending}
            isLimitReached={isLimitReached}
            onEdit={handleEdit}
            onArchive={handleArchive}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={TabsSchema.Enum.archived}>
          <CustomFieldTable
            variant="archived"
            fields={archivedFields}
            isLoading={customFieldsQuery.isPending}
            isLimitReached={isLimitReached}
            onRestore={handleRestore}
          />
        </TabPanel>
      </Box>

      <CreateFieldModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSuccess={handleCreateSuccess}
      />

      <EditFieldModal
        open={!!editingField}
        field={editingField}
        onClose={handleCloseEditModal}
        onSuccess={handleEditSuccess}
      />

      <ArchiveFieldModal
        open={!!archivingField}
        field={archivingField}
        onClose={handleCloseArchiveModal}
        onSuccess={handleArchiveSuccess}
      />

      <RestoreFieldModal
        open={!!restoringField}
        field={restoringField}
        onClose={handleCloseRestoreModal}
        onSuccess={handleRestoreSuccess}
      />
    </Stack>
  );
};

export const CustomFieldsPage: React.FC = function CustomFieldsPage() {
  return (
    <PageLayout>
      <Suspense fallback={<BarLoading />}>
        <CustomFieldsPageInternal />
      </Suspense>
    </PageLayout>
  );
};
